import {
  ApiError,
  BarChart,
  BarChartCoordinates,
  BarChartDataset,
  BarChartProps,
  CenteredLoader,
  Theme,
  TooltipItem,
  TooltipModel,
  useTheme,
} from "@shared/primitive-components"
import * as DateFns from "date-fns"
import { useMemo } from "react"
import { localDateTimeFromDate } from "@flockcover/flock-svc-core-types"
import { GetPeriodsOutput } from "../../../api/insights/periods/utils"
import { InsightsPeriod, InsightsPeriodNumber } from "../../../api/insights"
import { useGetInitialScores } from "../../../api/rebates/useGetInitialScores"
import { useInsights, usePolicyAndFleet } from "../../../contexts"
import { chartAnnotation, inDateTimeRange, reduceDateByOneHour, threshold, toPeriodLabel } from "../../../utils"
import { fleetSafetyThreshold } from "../../const"

export const SAFETY_SCORE_OVERVIEW_CHART_DTI = `safety_score_overview_chart`

export type SafetyScoreOverviewChartProps = {
  periods: GetPeriodsOutput
  target: number | null
}

export const getBackgroundColor = (_value: number | null, theme: Theme) => {
  if (_value === null) return theme.palette.grey.dark
  const value = Math.round(_value)
  if (value >= fleetSafetyThreshold.low) return theme.palette.full.green
  if (value >= fleetSafetyThreshold.medium) return theme.palette.full.amber
  if (value >= fleetSafetyThreshold.high) return theme.palette.full.orange
  if (value >= fleetSafetyThreshold.severe) return theme.palette.full.red
  return theme.palette.grey.dark
}

const Chart = ({ periods, target }: SafetyScoreOverviewChartProps) => {
  const theme = useTheme()
  const chart = useMemo<BarChartProps<BarChartCoordinates<number | null>[], string[]>>(() => {
    // Build datasets
    const labels = periods.periods.map((period) => toPeriodLabel(period.startedAt, reduceDateByOneHour(period.endedAt)))
    const periodsDatesets = periods.periods.map((it, i) => {
      const val = it.analytics?.score
      return {
        x: labels[i],
        y: val ? Math.round(val) : null,
      }
    })
    const barsBackgroundColor = periods.periods.map((period, i) =>
      i !== periods.currentPeriodIndex
        ? theme.palette.full.blue
        : getBackgroundColor(period.analytics?.score ?? null, theme)
    )
    const datasets: BarChartDataset<BarChartCoordinates<number | null>[]> = [
      {
        data: periodsDatesets,
        datasetOptions: {
          backgroundColor: barsBackgroundColor,
          barThickness: `flex`,
          barPercentage: 0.95,
          categoryPercentage: 1,
        },
      },
    ]

    // Build tooltips
    const data = periods.periods.map((p) => {
      const val = p.analytics?.score ?? 0
      const { color, label, labelColor } = threshold(val, val, val, theme)
      return {
        color,
        label,
        labelColor,
        value: 1,
      }
    })
    const toolTipLabelFn = function (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">) {
      const { dataIndex } = tooltipItem
      const { label } = data[dataIndex]
      return label ? `${label} Risk`.toUpperCase() : ``
    }

    const toolTipTitleFn = function (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">[]) {
      const item = tooltipItem[0]
      const { raw } = item
      const coordinate = raw as BarChartCoordinates<number>
      return `${coordinate.x[0]} ${coordinate.x[1]}`
    }

    const toolTipBeforeBodyFn = function (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">[]) {
      const item = tooltipItem[0]
      const { raw } = item
      const coordinate = raw as BarChartCoordinates<number>
      return `${coordinate.y}`
    }

    const toolTipLabelColorFn = function (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">) {
      const { dataIndex } = tooltipItem
      const { labelColor } = data[dataIndex]
      return labelColor
    }

    // Build chart details
    const xAxisTickColor =
      periods.periods.length > 0
        ? periods.periods.map((p) =>
            inDateTimeRange(p.startedAt, p.endedAt) ? theme.palette.full.blue : theme.palette.coal.light
          )
        : theme.palette.coal.light

    const annotations = target
      ? {
          annotations: {
            label1: {
              type: `label`,
              xValue: labels[0],
              yValue: target - 2,
              backgroundColor: theme.palette.coal.dark,
              position: {
                x: `start`,
                y: `start`,
              },
              color: theme.palette.white,
              font: {
                size: 12,
              },
              content: `Starting Safety Score (${target})`,
            },
            line1: chartAnnotation(undefined, target, undefined, target, true),
          },
        }
      : undefined

    return {
      annotations,
      datasets,
      height: 318,
      labels,
      maintainAspectRatio: false,
      responsive: true,
      title: `Safety Score`,
      toolTipBeforeBodyFn,
      toolTipLabelColorFn,
      toolTipLabelFn,
      toolTipTitleFn,
      xAxisTickColor,
      yAxisMaxValue: 100,
      yAxisStepSize: 25,
    }
  }, [periods, target, theme])

  return (
    <BarChart<BarChartCoordinates<number | null>[], string[]>
      chart={chart}
      dataTestId={SAFETY_SCORE_OVERVIEW_CHART_DTI}
    />
  )
}

export const SafetyScoreOverviewChart = () => {
  const { policy } = usePolicyAndFleet()
  const { periods, insightsProviderLoading } = useInsights()
  const [initialScores] = useGetInitialScores({ id: policy.id })

  if (initialScores.result?.tag === `err`) return <ApiError />

  if (insightsProviderLoading || initialScores.loading) return <CenteredLoader height="calc(100% - 80px)" />

  const initialScore = initialScores?.result?.data ?? null
  const target =
    initialScore !== null &&
    policy.meta?.productVersion !== `V2.0` &&
    policy.meta?.productVersion !== `V2.0-HavenV1.0` &&
    policy.meta?.productVersion !== `V2.0-AdmiralV1.0`
      ? Math.round(initialScore)
      : null

  return (
    <Chart
      periods={
        periods !== undefined && periods.periods.length > 0
          ? fillInsightsPeriods(periods)
          : { periods: [], currentPeriodIndex: 0 }
      }
      target={target}
    />
  )
}

export function fillInsightsPeriods(input: GetPeriodsOutput): GetPeriodsOutput {
  const { periods } = input
  if (periods.length >= 12) {
    return input
  }

  // Determine the start month (from inception date or first period)
  const startDate = periods[0].startedAt
  // Create array of all 12 months starting from the start date
  const allPeriods: Array<InsightsPeriod> = []
  let currentDate = startDate

  for (let i = 0; i < 13; i++) {
    const periodNumber = i

    // Check if we have an existing period for this month
    const existingPeriod = periods.find((p) => p.number === periodNumber)

    if (existingPeriod) {
      allPeriods.push(existingPeriod)
    } else {
      const monthEnd = DateFns.endOfMonth(new Date(currentDate))
      // Create an empty period for this month
      // const monthEnd = currentDate.plusMonths(1).minusDays(1)
      allPeriods.push({
        number: periodNumber as InsightsPeriodNumber,
        startedAt: currentDate,
        endedAt: localDateTimeFromDate(new Date(monthEnd), `Europe/London`),
        analytics: null,
      })
    }

    currentDate = localDateTimeFromDate(DateFns.addMonths(new Date(currentDate), 1), `Europe/London`)
  }

  return {
    periods: allPeriods,
    currentPeriodIndex: getCurrentPeriodIndex(allPeriods),
  }
}

const getCurrentPeriodIndex = (result: ReadonlyArray<InsightsPeriod>): number => {
  const i = result.findIndex((period) => {
    const today = localDateTimeFromDate(new Date(), `Europe/London`)
    // If period end time is greater than today's date, we are in that period
    return period.endedAt > today
  })
  // If today is greater than all period end times, the policy has finished and we should select the last period
  return i !== -1 ? i : result.length - 1
}
